import { ReactNode, useId } from "react";
import {
  StoreSeller,
  CartItem,
  PaymentMethod,
  PaymentMethodLabel,
} from "../../models";
import { Button } from "../../components/ui/Button";
import { formatCurrency } from "../../utils/currency";
import { CheckoutTotals } from "../../utils/pricing";
import { ExchangeIcon, WhatsAppIcon } from "./CheckoutIcons";
import { OrderTotals } from "./OrderSummary";
import {
  CHECKOUT_PRIMARY_ACTION_CLASS,
  CHECKOUT_SIDEBAR_GRID_CLASS,
} from "./styles";
import { StepBackButton } from "./StepBackButton";
import { GuestInfo, PaymentEntry } from "./types";

const NEXT_STEPS = [
  "Você envia o pedido.",
  "A vendedora confirma o frete com você pelo WhatsApp.",
  "Você paga e a peça segue para você.",
];

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-champagne-dark/60 mb-4">
      <h2 className="text-sm font-bold uppercase tracking-wider text-chocolate-dark mb-1">
        {title}
      </h2>
      <span className="block w-6 h-0.5 bg-chocolate rounded-full mb-3" />
      {children}
    </div>
  );
}

interface ConfirmStepProps {
  seller: StoreSeller;
  paymentEntries: PaymentEntry[];
  totals: CheckoutTotals;
  installmentsInterestFree: boolean;
  observation: string;
  onObservationChange: (v: string) => void;
  items: CartItem[];
  onSubmit: () => void;
  submitting: boolean;
  customerInfo?: GuestInfo;
  backLabel: string;
  onBack: () => void;
}

export function ConfirmStep({
  seller,
  paymentEntries,
  totals,
  installmentsInterestFree,
  observation,
  onObservationChange,
  items,
  onSubmit,
  submitting,
  customerInfo,
  backLabel,
  onBack,
}: ConfirmStepProps) {
  const observationId = useId();
  const nextStepsId = useId();

  return (
    <>
      <div className={CHECKOUT_SIDEBAR_GRID_CLASS}>
        <aside className="lg:order-2 lg:sticky lg:top-20">
          <SectionCard title="Resumo do pedido">
            <div className="flex flex-col gap-2 mb-3">
              {items.map((item) => (
                <div
                  key={`${item.productId}-${item.variantId}`}
                  className="flex justify-between gap-2 text-base"
                >
                  <span className="text-text-medium min-w-0 break-words">
                    {item.productName} ({item.variantLabel}) x{item.quantity}
                    {item.isGift && (
                      <svg
                        className="w-3.5 h-3.5 ml-1 inline-block text-chocolate-dark"
                        aria-label="Presente"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18C2.754 7.5 2.25 8.004 2.25 8.625v1.5c0 .621.504 1.125 1.125 1.125z"
                        />
                      </svg>
                    )}
                  </span>
                  <span className="font-medium text-text-dark flex-shrink-0">
                    R${" "}
                    {formatCurrency(
                      (item.promotionalPrice ?? item.price) * item.quantity,
                    )}
                  </span>
                </div>
              ))}
            </div>
            <div className="border-t border-champagne pt-3">
              <OrderTotals totals={totals} />
            </div>
          </SectionCard>
        </aside>

        <div className="lg:order-1">
          {customerInfo && (
            <SectionCard title="Seus dados">
              <div className="flex flex-col gap-1.5 text-base">
                <div className="flex justify-between">
                  <span className="text-text-medium">Nome</span>
                  <span className="font-medium text-text-dark">
                    {customerInfo.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-medium">Celular</span>
                  <span className="font-medium text-text-dark">
                    {customerInfo.phone}
                  </span>
                </div>
                {customerInfo.street && (
                  <div className="flex justify-between">
                    <span className="text-text-medium">Endereço</span>
                    <span className="font-medium text-text-dark text-right">
                      {customerInfo.street}, {customerInfo.number}
                      {customerInfo.complement &&
                        ` - ${customerInfo.complement}`}
                    </span>
                  </div>
                )}
                {customerInfo.neighborhood && (
                  <div className="flex justify-between">
                    <span className="text-text-medium">Bairro</span>
                    <span className="font-medium text-text-dark">
                      {customerInfo.neighborhood}
                    </span>
                  </div>
                )}
                {customerInfo.city && (
                  <div className="flex justify-between">
                    <span className="text-text-medium">Cidade</span>
                    <span className="font-medium text-text-dark">
                      {customerInfo.state
                        ? `${customerInfo.city}/${customerInfo.state}`
                        : customerInfo.city}
                    </span>
                  </div>
                )}
                {customerInfo.zipCode && (
                  <div className="flex justify-between">
                    <span className="text-text-medium">CEP</span>
                    <span className="font-medium text-text-dark">
                      {customerInfo.zipCode}
                    </span>
                  </div>
                )}
                {!customerInfo.street &&
                  !customerInfo.neighborhood &&
                  !customerInfo.city && (
                    <p className="text-sm text-text-medium mt-1">
                      Endereço já cadastrado será utilizado.
                    </p>
                  )}
              </div>
            </SectionCard>
          )}

          <SectionCard title="Vendedora">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-champagne flex items-center justify-center text-chocolate-dark font-bold flex-shrink-0">
                {seller.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-semibold text-text-dark">
                  {seller.name}
                </p>
                <p className="text-sm text-text-medium">
                  {seller.whatsAppPhone}
                </p>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Formas de pagamento">
            <div className="flex flex-col gap-1">
              {paymentEntries.map((e) => (
                <div
                  key={e.id}
                  className="flex justify-between gap-2 text-base"
                >
                  <span className="text-text-medium min-w-0 break-words">
                    {PaymentMethodLabel[e.paymentMethod]}
                    {e.paymentMethod === PaymentMethod.CreditCard &&
                      (e.installments ?? 1) > 1 &&
                      ` — ${e.installments}x de R$ ${formatCurrency(e.amountPaid / (e.installments ?? 1))}${installmentsInterestFree ? " sem juros" : ""}`}
                  </span>
                  <span className="font-medium text-text-dark flex-shrink-0">
                    R$ {formatCurrency(e.amountPaid)}
                  </span>
                </div>
              ))}
            </div>
          </SectionCard>

          <div className="mb-4">
            <label
              htmlFor={observationId}
              className="block text-sm font-medium text-text-dark mb-1"
            >
              Observação (opcional)
            </label>
            <textarea
              id={observationId}
              value={observation}
              onChange={(e) => onObservationChange(e.target.value)}
              placeholder="Ex: observação do pedido..."
              rows={3}
              className="w-full px-3 py-2.5 rounded-lg border border-champagne-dark text-text-dark placeholder-text-light bg-white text-base focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate resize-none"
            />
          </div>

          <section
            aria-labelledby={nextStepsId}
            className="bg-champagne/60 rounded-xl p-4 mb-4 border border-champagne-dark/30"
          >
            <h2
              id={nextStepsId}
              className="flex items-center gap-2 text-sm font-bold text-chocolate-dark mb-3"
            >
              <WhatsAppIcon />
              Como funciona daqui para frente
            </h2>
            <ol className="flex flex-col gap-2.5">
              {NEXT_STEPS.map((text, index) => (
                <li key={text} className="flex items-start gap-2.5 text-sm text-text-dark">
                  <span
                    aria-hidden="true"
                    className="w-6 h-6 rounded-full bg-chocolate-dark text-white flex items-center justify-center text-xs font-bold flex-shrink-0"
                  >
                    {index + 1}
                  </span>
                  <span className="pt-0.5">{text}</span>
                </li>
              ))}
            </ol>
          </section>

          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mb-3">
            <span className="flex items-center gap-1.5 text-sm text-text-medium">
              <ExchangeIcon />
              Troca fácil em 7 dias
            </span>
            <span className="flex items-center gap-1.5 text-sm text-text-medium">
              <WhatsAppIcon />
              Suporte via WhatsApp
            </span>
          </div>

          <div className="flex flex-col gap-1">
            <Button
              onClick={onSubmit}
              loading={submitting}
              size="lg"
              className={`w-full ${CHECKOUT_PRIMARY_ACTION_CLASS}`}
            >
              Enviar pedido
            </Button>
            <StepBackButton label={backLabel} onClick={onBack} />
          </div>
        </div>
      </div>
    </>
  );
}
